import { useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Upload, X, FileText, Image as ImageIcon, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable file upload widget with drag-and-drop, progress bar, filename preview
 * and inline error surfacing. Wraps the Emergent Object Storage endpoints so
 * every form that needs file capture uses the same shape.
 *
 * Props:
 *   endpoint        API path for the multipart upload (defaults to /files/upload).
 *                   Pass "/hrm/postings/{id}/upload" for public job-application uploads.
 *   accept          MIME/type filter for the native file picker (e.g. ".pdf,.docx").
 *   maxSizeMb       Client-side size guard before the upload starts (server also enforces).
 *   value           { id, original_filename } or null. If provided, shows an existing file.
 *   onChange({file}) Called with the file record after a successful upload OR null on remove.
 *   label           Text shown inside the drop zone.
 *   downloadUrl     Optional URL for the "view" link on a completed upload.
 */
export default function FileUpload({
  endpoint = "/files/upload",
  accept = ".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.txt,.rtf",
  maxSizeMb = 10,
  value = null,
  onChange,
  label = "Attach a file",
  downloadUrl,
  testid,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [err, setErr] = useState("");

  const doUpload = async (file) => {
    setErr("");
    if (file.size > maxSizeMb * 1024 * 1024) {
      setErr(`File is ${(file.size / 1024 / 1024).toFixed(1)} MB — limit is ${maxSizeMb} MB.`);
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      onChange && onChange({ file: res.data });
    } catch (e) {
      const msg = e.response?.data?.detail || "Upload failed";
      setErr(typeof msg === "string" ? msg : "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const onFilePicked = (e) => {
    const f = e.target.files?.[0];
    if (f) doUpload(f);
    e.target.value = ""; // allow re-selecting the same file
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) doUpload(f);
  };

  // Completed state — show filename + view + remove
  if (value?.id) {
    return (
      <div className="border border-white/12 bg-white/[0.02] p-3 flex items-center gap-3" data-testid={testid ? `${testid}-attached` : undefined}>
        <span className="w-8 h-8 border border-[color:var(--cobalt)]/50 flex items-center justify-center text-[color:var(--cobalt)] flex-shrink-0">
          {isImage(value.original_filename) ? <ImageIcon size={14}/> : <FileText size={14}/>}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-white/90 truncate">{value.original_filename || "attached file"}</div>
          <div className="font-mono text-[10px] text-white/50 uppercase tracking-widest flex items-center gap-2">
            <CheckCircle2 size={11} className="text-[color:var(--cobalt)]"/>
            Uploaded {value.size ? `· ${(value.size/1024).toFixed(0)} kb` : ""}
          </div>
        </div>
        {downloadUrl && (
          <a href={downloadUrl} target="_blank" rel="noopener noreferrer" className="btn-cta ghost text-xs">View</a>
        )}
        <button type="button" onClick={()=>{ onChange && onChange({ file: null }); }} className="btn-cta ghost text-xs" data-testid={testid ? `${testid}-remove` : undefined}>
          <X size={12}/>
        </button>
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        disabled={uploading}
        data-testid={testid}
        className={`w-full border ${dragOver ? "border-[color:var(--cobalt)] bg-[color:var(--cobalt)]/[0.05]" : "border-dashed border-white/20 bg-white/[0.015]"} p-5 flex flex-col items-center justify-center gap-2 hover:border-[color:var(--cobalt)]/50 transition-colors`}
      >
        {uploading ? (
          <>
            <div className="font-mono text-xs text-[color:var(--cobalt)]">Uploading… {progress}%</div>
            <div className="w-40 h-0.5 bg-white/10 mt-1">
              <div className="h-full bg-[color:var(--cobalt)]" style={{ width: `${progress}%` }}/>
            </div>
          </>
        ) : (
          <>
            <Upload size={18} className="text-white/50"/>
            <div className="text-sm text-white/80">{label}</div>
            <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">
              drop file or click · max {maxSizeMb} mb
            </div>
          </>
        )}
      </button>
      <input ref={inputRef} type="file" accept={accept} onChange={onFilePicked} className="hidden"/>
      {err && <div className="text-[color:var(--signal)] text-xs mt-2">{err}</div>}
    </div>
  );
}

function isImage(name) {
  return name && /\.(png|jpe?g|webp|gif)$/i.test(name);
}

/** Helper: build an authenticated view URL for any /files/{id} record. */
export function fileDownloadUrl(fileId) {
  return `${API}/files/${fileId}`;
}
